import { useRef } from 'react'
import stringify from 'fast-json-stable-stringify'

const getFieldsHash = (
    data: Record<string, unknown> = {},
    fields: string[] = [],
    prefix = ''
): string => {
    let hash = prefix
    if (fields.length > 0 && data) {
        fields.forEach((field) => {
            if (typeof data[field] !== 'undefined') {
                hash += hashCode(stringify(data[field]))
            }
        })
    } else {
        hash += hashCode(stringify(data))
    }

    return hash
}

const hashCode = (string: string) => {
    let hash = 0
    for (let i = 0; i < string.length; i++) {
        const char = string.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash
    }
    return hash
}

type UseObjectCompare = (
    initialObjectState?: Record<string, unknown>,
    fieldsToTrack?: string[],
    hashPrefix?: string
) => (
    newObject?: Record<string, unknown>,
    updateStoredState?: boolean
) => boolean

/**
 * Based on the provided list of fields, remember their hashed values on an object.
 * After that - compare the new object with hash to decide whether it has changes
 *
 * @param initialObjectState
 * @param fieldsToTrack
 * @param hashPrefix
 */
export const useObjectCompare: UseObjectCompare = (
    initialObjectState,
    fieldsToTrack = [],
    hashPrefix = ''
) => {
    const objectHash = useRef(
        initialObjectState
            ? getFieldsHash(initialObjectState, fieldsToTrack, hashPrefix)
            : Number.MIN_SAFE_INTEGER
    )

    return (
        newObject?: Record<string, unknown>,
        updateStoredState = false
    ): boolean => {
        const newObjectHash = getFieldsHash(
            newObject,
            fieldsToTrack,
            hashPrefix
        )

        const isChanged = objectHash.current !== newObjectHash

        if (updateStoredState && isChanged) {
            objectHash.current = newObjectHash
        }

        return isChanged
    }
}
